import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { defineMessages, useIntl } from 'react-intl';
import ScreenReaderContent from 'components/shared/DesignSystem/shared/components/ScreenReaderContent';
// #region styled components
const Container = styled.div`
  position: relative;
  aspect-ratio: 1/1.1;
  display: grid;
  gap: 3%;
  grid-template-rows: 90% 7%;
`;
const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--cc_size_border_radius_l);
`;
const IconContainer = styled.span`
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 100%;
  border-width: var(--cc_size_border_width_m);
  border-style: solid;
  transition: transform 0.2s ease-out;

  ${(props) =>
    props.isChecked
      ? css`
          background-color: var(--cc_color_button_background_primary);
          border-color: var(--cc_color_button_background_primary);
          color: var(--cc_color_button_text_primary);
        `
      : css`
          border-color: rgba(255, 255, 255, 0.6);
        `}
`;
const Icon = styled.svg`
  width: 12px;
  height: 12px;
  fill: currentColor;
`;
const ImageOverlay = styled.span`
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--cc_size_spacing_s);
  pointer-events: none;
  border-radius: var(--cc_size_border_radius_l);
  ${(props) =>
    props.isChecked
      ? css`
          opacity: 1;
          background: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0) 0%,
            var(--cc_color_link_primary_alpha_80) 100%
          );
        `
      : css`
          opacity: 0;
          background: var(--cc_color_background_backdrop_alpha_70);
        `}
`;
const Label = styled.label`
  all: unset;
  display: block;
  position: relative;
  cursor: pointer;
  border-radius: var(--cc_size_border_radius_l);
  box-shadow: 0 var(--cc_size_depth_1_y) var(--cc_size_depth_1_blur)
    var(--cc_color_depth_1);
  transition: transform 0.2s ease-out;
  &:hover ${ImageOverlay} {
    opacity: 1;
  }
  &:active {
    transform: scale(0.95);
    ${IconContainer} {
      transform: scale(1.75);
    }
  }
  ${(props) =>
    props.isDisabled
      ? css`
          opacity: 0.5;
          pointer-events: none;
        `
      : null}
`;
const Input = styled.input`
  margin: -1px;
  padding: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip: rect(0, 0, 0, 0);
  position: absolute;
  &:focus-visible ~ ${Label} {
    outline: var(--cc_size_border_width_l) solid var(--cc_color_link_primary);
  }
`;
const Caption = styled.span`
  display: flex;
  align-items: center;
  gap: var(--cc_size_spacing_xs);
  min-width: 1px;
  margin-top: var(--cc_size_spacing_xs);
  color: var(--cc_color_text_subtle);
  font-size: ${(props) =>
    props.assetSize > 300 ? 'var(--cc_size_text_s)' : 'var(--cc_size_text_xs)'};
`;
const CaptionText = styled.span`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const CaptionVideoIcon = styled.i`
  font-size: var(--cc_size_text_m);
  color: var(--cc_color_text_nonessential);
`;
const DaysToPurge = styled.span`
  position: absolute;
  inset-inline-end: 0;
  inset-block-end: 0;
  display: flex;
  align-items: center;
  gap: var(--cc_size_spacing_xs);
  border-radius: var(--cc_size_border_radius_l) 0 var(--cc_size_border_radius_l);
  font-size: var(--cc_size_text_xxs);
  font-weight: 700;
  text-transform: uppercase;
  padding: var(--cc_size_spacing_xs) var(--cc_size_spacing_s);
  background: ${(props) =>
    props.daysToPurge <= 5
      ? 'var(--cc_color_button_background_destroy)'
      : 'var(--cc_color_button_background_subtle)'};
  color: ${(props) =>
    props.daysToPurge <= 5
      ? 'var(--cc_color_button_text_destroy)'
      : 'var(--cc_color_button_text_subtle)'};
`;
const DaysToPurgeIcon = styled.i`
  font-size: var(--cc_size_text_s);
`;
// #endregion

const messages = defineMessages({
  daysToPurgeCount: {
    id: 'Project.trashCan.daysToPurgeCount',
    defaultMessage: '{count} Days',
    description: 'Count for number of days before item is deleted',
  },
  screenreaderPurgeCount: {
    id: 'Project.trashCan.screenreaderPurgeCount',
    defaultMessage: 'This item will be deleted in {count} days',
    description:
      'Screenreader message telling how many days are left before item is deleted',
  },
  screenReaderPurgeToday: {
    id: 'Project.trashCan.screenReaderPurgeToday',
    defaultMessage: 'This item will be deleted today',
    description:
      'Screenreader message explaing that the item will be deleted today',
  },
  purgeToday: {
    id: 'Project.trashCan.purgeToday',
    defaultMessage: 'Today',
    description: 'Message that item will be deleted',
  },
  videoIconTitle: {
    id: 'Project.trashCan.videoIconTitle',
    defaultMessage: 'This is a video',
    description: 'Message to clarify video icon in caption',
  },
});

const Asset = ({
  onClick,
  onChange,
  onFocus,
  onMouseOut,
  onMouseOver,
  onBlur,
  url,
  isVideo,
  label,
  checked,
  disabled,
  id,
  size,
  caption,
  daysToPurge,
}) => {
  const { formatMessage } = useIntl();

  const handleChange = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onChange(e);
  };
  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick(e);
  };
  const renderPurgeDays = () => {
    if (daysToPurge === 0) {
      return (
        <DaysToPurge daysToPurge={daysToPurge}>
          <ScreenReaderContent>
            {formatMessage(messages.screenReaderPurgeToday)}
          </ScreenReaderContent>
          <DaysToPurgeIcon
            className="mdi mdi-delete-empty"
            aria-hidden="true"
          />
          {formatMessage(messages.purgeToday)}
        </DaysToPurge>
      );
    } else if (daysToPurge) {
      return (
        <DaysToPurge daysToPurge={daysToPurge}>
          <ScreenReaderContent>
            {formatMessage(messages.screenreaderPurgeCount, {
              count: daysToPurge,
            })}
          </ScreenReaderContent>
          <DaysToPurgeIcon
            className="mdi mdi-delete-empty"
            aria-hidden="true"
          />
          {formatMessage(messages.daysToPurgeCount, {
            count: daysToPurge,
          })}
        </DaysToPurge>
      );
    } else {
      return null;
    }
  };
  return (
    <Container data-testid={id} size={size}>
      <Input
        id={id}
        type="checkbox"
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={handleChange}
        onClick={handleClick}
        checked={checked}
        disabled={disabled}
      />
      <Label
        htmlFor={id}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        isDisabled={disabled}
      >
        <ScreenReaderContent>{label}</ScreenReaderContent>
        <Image src={url} alt="" />
        {renderPurgeDays()}
        {isVideo ? (
          <ScreenReaderContent>Video play icon</ScreenReaderContent>
        ) : null}
        <ImageOverlay isChecked={checked}>
          <IconContainer aria-hidden="true" isChecked={checked}>
            {checked ? (
              <Icon viewBox="0 0 24 24">
                <rect
                  x="20.1216"
                  y="3.88324"
                  width="4.23919"
                  height="20.1362"
                  transform="rotate(45 20.1216 3.88324)"
                />
                <rect
                  x="1.38687"
                  y="13.6253"
                  width="4.23919"
                  height="6.35879"
                  transform="rotate(-45 1.38687 13.6253)"
                />
              </Icon>
            ) : null}
          </IconContainer>
        </ImageOverlay>
      </Label>
      <Caption assetSize={size}>
        {isVideo ? (
          <CaptionVideoIcon
            className="mdi mdi-video"
            title={formatMessage(messages.videoIconTitle)}
          />
        ) : null}
        <CaptionText>{caption}</CaptionText>
      </Caption>
    </Container>
  );
};

Asset.propTypes = {
  url: PropTypes.string,
  id: PropTypes.string.isRequired,
  isVideo: PropTypes.bool,
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onMouseOver: PropTypes.func,
  onMouseOut: PropTypes.func,
  size: PropTypes.number,
  caption: PropTypes.string,
  daysToPurge: PropTypes.number,
};
Asset.defaultProps = {
  isVideo: false,
  url: undefined,
  checked: false,
  disabled: false,
  onChange: () => {},
  onClick: () => {},
  onFocus: () => {},
  onBlur: () => {},
  onMouseOver: () => {},
  onMouseOut: () => {},
  size: 140,
  caption: undefined,
  daysToPurge: undefined,
};
export default Asset;
